import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import useFetch from '../../hooks/useFetch';
import { Radar, Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns"; 
import Chart from "chart.js/auto"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { allSets as englishSets} from "./msmeindexData";
import { allSets as hindiSets} from "./msmeindexDataHindi";


export default function MSMECompliances() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState("all"); // all / my / history
  const [showExample, setShowExample] = useState(false); 
  const [viewAll, setViewAll] = useState(false); 
  
  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const compliances = [
    // ——————————————— CENTRAL / NATIONAL COMPLIANCES ———————————————
    "Udyam Registration (Central)",
    "GST Registration & Returns (Central)",
    "Income Tax / TDS / Advance Tax (Central)",
    "EPF Registration & Monthly Filing (Central)",
    "ESI Registration & Returns (Central)",
    "Annual Audit / Books of Account (Central)",
    "E‑Invoicing (if applicable) (Central)",
    "Importer‑Exporter Code (IEC) (Central)",
    "Labour Codes Compliance (Central)",

    // ——————————————— SECTOR‑SPECIFIC COMPLIANCES ———————————————
    // 🍎 Food & Beverages
    "FSSAI – Basic Registration (Food)",
    "FSSAI – State License (Food)",
    "FSSAI – Central License (Large Food Units)",

    // 💊 Pharma & Healthcare
    "Drug License – State Drug Authority (Pharma)",
    "CDSCO Registration (Medical Devices/Drugs)",
    "Good Manufacturing Practices (GMP) Compliance",

    // 🏭 Manufacturing
    "Factory Licence / Inspectorate (Manufacturing)",
    "BIS Certification – Product Standard (Manufacturing)",
    "Hazardous Waste Handling License (Manufacturing)",
    "Trade Effluent Consent (SPCB) (Manufacturing)",

    // 🏗️ Construction
    "RERA Registration (Construction / Real Estate)",
    "Construction Labour Welfare Fund (Labour)",
    "Building Plan Approval (Local Municipality)",

    // 🖥️ IT & Services
    "Professional Tax (if applicable) (Services)",
    "Data Protection / IT Rules Compliance (Services)",

    // 🚚 Logistics & Transport
    "Transport / Vehicle Permits (Transport)",
    "Goods Vehicle Tax (State Transport)",

    // ——————————————— STATE‑SPECIFIC COMPLIANCES ———————————————
    // 📍 West Bengal (WB)
    "Shop & Establishment – WB",
    "Professional Tax – WB",
    "Trade License – Kolkata Municipal Corporation (WB)",
    "Factory License – WB Inspectorate",
    "West Bengal Pollution Control Board Consent (WBSPCB)",

    // 📍 Maharashtra (MH)
    "Shop & Establishment – MH",
    "Professional Tax – MH",
    "Trade License – Brihanmumbai Municipal Corporation (MH)",
    "Factory License – MH Inspectorate",
    "Maharashtra Pollution Control Board Consent (MPCB)",

    // 📍 Karnataka (KA)
    "Shop & Establishment – KA",
    "Professional Tax – KA",
    "Trade License – Bruhat Bengaluru Mahanagara Palike (KA)",
    "Factory License – KA Inspectorate",
    "Karnataka State Pollution Control Board Consent (KSPCB)",

    // 📍 Tamil Nadu (TN)
    "Shop & Establishment – TN",
    "Professional Tax – TN",
    "Trade License – Chennai Corporation (TN)",
    "Factory License – TN Inspectorate",
    "Tamil Nadu Pollution Control Board Consent (TNPCB)",

    // 📍 Gujarat (GJ)
    "Shop & Establishment – GJ",
    "Professional Tax – GJ",
    "Trade License – Ahmedabad Municipal Corporation (GJ)",
    "Factory License – GJ Inspectorate",
    "Gujarat Pollution Control Board Consent (GPCB)",

    // 📍 Delhi (DL)
    "Shop & Establishment – DL",
    "Professional Tax – DL (if applicable)",
    "Trade License – MCD / NDMC / SDMC (DL)",
    "Factory License – DL Inspectorate",
    "Delhi Pollution Control Committee Consent (DPCC)",

    // 📍 Uttar Pradesh (UP)
    "Shop & Establishment – UP",
    "Professional Tax – UP",
    "Trade License – Local Municipality (UP)",
    "Factory License – UP Inspectorate",
    "Uttar Pradesh Pollution Control Board Consent (UPPCB)",

    // 📍 Andhra Pradesh (AP)
    "Shop & Establishment – AP",
    "Professional Tax – AP",
    "Trade License – Local Municipality (AP)",
    "Factory License – AP Inspectorate",
    "Andhra Pradesh Pollution Control Board Consent (APPCB)",

    // 📍 Telangana (TG)
    "Shop & Establishment – TG",
    "Professional Tax – TG",
    "Trade License – GHMC / Local Municipality (TG)",
    "Factory License – TG Inspectorate",
    "Telangana Pollution Control Board Consent (TSPCB)",

    // 📍 Rajasthan (RJ)
    "Shop & Establishment – RJ",
    "Professional Tax – RJ",
    "Trade License – Local Municipality (RJ)",
    "Factory License – RJ Inspectorate",
    "Rajasthan State Pollution Control Board Consent (RSPCB)",

    // 📍 Bihar (BR)
    "Shop & Establishment – BR",
    "Professional Tax – BR",
    "Trade License – Local Municipality (BR)",
    "Factory License – BR Inspectorate",
    "Bihar State Pollution Control Board Consent (BSPCB)",

    // ——————————————— OTHER STATE‑SPECIFIC OR LOCAL VARIANTS ———————————————
    "Fire Safety NOC — Local Fire Dept (State/City)",
    "Local Health Officer Approval (City / Urban Body)",
    "Trade Effluent Discharge NOC (Local, if separate)",
    "District Industrial Centre clearances (Local)",
    "State Single Window clearance (where available)",
  ];

  const [selectedCompliances, setSelectedCompliances] = useState([]);

  const toggleCompliance = (item) => {
      setSelectedCompliances((prev) =>
        prev.includes(item)
          ? prev.filter((c) => c !== item)
          : [...prev, item]
      );
    };

  const [expanded, setExpanded] = useState(
    selectedCompliances.map(() => true)
  );

  const [statusFilter, setStatusFilter] = useState("All");

  const toggleCard = (index) => {
    setExpanded((prev) =>
      prev.map((val, i) => (i === index ? !val : val))
    );
  };

  React.useEffect(() => {
    setExpanded(selectedCompliances.map(() => true));
  }, [selectedCompliances]);

  React.useEffect(() => {
    if (viewMode === "my") {
      setViewAll(false); // hide "All Compliance" list
    } else {
      setViewAll(true); // show it again
    }
  }, [viewMode]);

  return (
    <div className="bg-black">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-m lg:text-xl font-bold text-yellow-200">SAKSHAM MSME Indian Compliance Checklist</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-8 mt-4 p-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="w-8 px-1 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                🔐
              </button>
            </>
          )}
        </div>
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs font-medium">{userName}</div>
              <div className="flex gap-3">
                <button
                  onClick={handleLogout}
                  className="w-8 p-2 rounded-md bg-red-600 hover:bg-red-700"
                >
                  ⏻
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-teal-300 w-8">🔐</Link>
            </div>
          )}
        </div>
      )}
       
      <div className="bg-black p-6 min-h-[75vh]">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
          <button
            className={`p-3 rounded-md font-semibold ${
              viewMode === "all" ? "bg-blue-600 text-white" : "bg-gray-800 text-white"
            } hover:bg-blue-500`}
            onClick={() => setViewAll(!viewAll)}
          >
            Show All Compliance
          </button>
          <button
            className={`p-3 rounded-md font-semibold ${
              viewMode === "my" ? "bg-blue-600 text-white" : "bg-gray-800 text-white"
            } hover:bg-blue-500`}
            onClick={() => setViewMode("my")}
          >
            Show My Compliance
          </button>
          <button
            className={`p-3 rounded-md font-semibold ${
              viewMode === "history" ? "bg-blue-600 text-white" : "bg-gray-800 text-white"
            } hover:bg-blue-500`}
            onClick={() => setViewMode("history")}
          >
            My Compliance History
          </button>
        </div>

        {viewAll && (
          <div className="flex flex-col space-y-1">
            {compliances.map((item) => (
              <label
                key={item}
                className="flex items-center space-x-3 bg-gray-900 p-2 rounded-md hover:bg-gray-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedCompliances.includes(item)}
                  onChange={() => toggleCompliance(item)}
                  className="w-5 h-5 text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-2 focus:ring-blue-400"
                />
                <span className="text-white">{item}</span>
              </label>
            ))}
          </div>
        )}
        {viewMode === "my" && selectedCompliances.length > 0 && (
          <>
            {/* Status Filter */}
            <div className="mb-4">
              <select
                className="w-full sm:w-1/3 h-12 p-2 rounded-md bg-gray-800 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Submitted">Submitted</option>
                <option value="Approved">Approved</option>
                <option value="Expired">Expired</option>
                <option value="Renewed">Renewed</option>
              </select>
            </div>

            {/* Filtered Compliance Cards */}
            {selectedCompliances
              .filter((c) => statusFilter === "All" || c.status === statusFilter)
              .map((item, index) => (
                <div
                  key={item.name}
                  className="bg-black rounded-lg mt-5 shadow-lg p-4 border border-cyan-200 transition-transform hover:scale-[1.01]"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-cyan-200 font-bold text-sm lg:text-lg">{item}</h3>
                    <button
                      type="button"
                      onClick={() => toggleCard(index)}
                      className="text-xs lg:text-sm w-20 lg:w-24 bg-blue-700 text-white font-semibold hover:underline"
                    >
                      {expanded[index] ? "Collapse" : "Expand"}
                    </button>
                  </div>
                  {/* Action Buttons Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                    <button 
                      onClick={() => navigate("/msme-compliance-rules")} 
                      className="p-2 rounded-md bg-green-600 text-white font-semibold hover:bg-green-500">
                      Latest Rule
                    </button>
                    <button 
                      onClick={() => navigate("/msme-compliance-news")}
                      className="p-2 rounded-md bg-yellow-600 text-white font-semibold hover:bg-yellow-500">
                      Compliance News
                    </button>
                    <button 
                      onClick={() => navigate("/think5whys-consultants")}
                      className="p-2 rounded-md bg-purple-600 text-white font-semibold hover:bg-purple-500">
                      Search Consultant
                    </button>
                  </div>            

                  {expanded[index] && (
                    <div className="flex flex-col space-y-4">
                      {/* Section 1: Basic Info */}
                      <div className="bg-gray-800 p-3 rounded-md space-y-2">
                        <h4 className="text-blue-400 font-semibold text-sm sm:text-base">
                          Basic Info
                        </h4>
                        <input
                          type="text"
                          placeholder="Category (Central/Sector/State)"
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.category || ""}
                        />
                        <input
                          type="text"
                          placeholder="Sector"
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.sector || ""}
                        />
                        <input
                          type="text"
                          placeholder="State"
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.state || ""}
                        />
                      </div>

                      {/* Section 2: Authority & Registration */}
                      <div className="bg-gray-800 p-3 rounded-md space-y-2">
                        <h4 className="text-blue-400 font-semibold text-sm sm:text-base">
                          Authority & Registration
                        </h4>
                        <input
                          type="text"
                          placeholder="Issuing Authority"
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.issuingAuthority || ""}
                        />
                        <input
                          type="text"
                          placeholder="Registration Number"
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.registrationNumber || ""}
                        />
                      </div>

                      {/* Section 3: Status & Dates */}
                      <div className="bg-gray-800 p-3 rounded-md space-y-2">
                        <h4 className="text-blue-400 font-semibold text-sm sm:text-base">
                          Status & Dates
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <select
                            className="p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.status || ""}
                          >
                            <option>Pending</option>
                            <option>Submitted</option>
                            <option>Approved</option>
                            <option>Expired</option>
                            <option>Renewed</option>
                          </select>

                          <select
                            className="p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.submissionMethod || ""}
                          >
                            <option>Online</option>
                            <option>Offline</option>
                            <option>Hybrid</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <input
                            type="date"
                            placeholder="Application Date"
                            className="p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.applicationDate || ""}
                          />
                          <input
                            type="date"
                            placeholder="Approval Date"
                            className="p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.approvalDate || ""}
                          />
                          <input
                            type="date"
                            placeholder="Expiry Date"
                            className="p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.expiryDate || ""}
                          />
                        </div>
                        <select
                          className="p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.renewalFrequency || ""}
                        >
                          <option>Monthly</option>
                          <option>Quarterly</option>
                          <option>Annually</option>
                          <option>One-time</option>
                        </select>
                      </div>

                      {/* Section 4: Notes & Reminders */}
                      <div className="bg-gray-800 p-3 rounded-md space-y-2">
                        <h4 className="text-blue-400 font-semibold text-sm sm:text-base">
                          Notes & Reminders
                        </h4>
                        <textarea
                          placeholder="Notes"
                          className="w-full h-40 p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.notes || ""}
                        />
                        <input
                          type="text"
                          placeholder="Reminders (e.g., 7 days before due)"
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.reminders || ""}
                        />
                      </div>

                      {/* Section 5: Officer & Fee */}
                      <div className="bg-gray-800 p-3 rounded-md space-y-2">
                        <h4 className="text-blue-400 font-semibold text-sm sm:text-base">
                          Officer & Fee
                        </h4>
                        <input
                          type="text"
                          placeholder="Compliance Officer"
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.complianceOfficer || ""}
                        />
                        <input
                          type="number"
                          placeholder="Fee Paid"
                          className="w-40 p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          defaultValue={item.feePaid || ""}
                        />
                      </div>

                      {/* Section 6: Attachments & Alerts */}
                      <div className="bg-gray-800 p-3 rounded-md space-y-2">
                        <h4 className="text-blue-400 font-semibold text-sm sm:text-base">
                          Attachments & Alerts
                        </h4>
                        <input
                          type="file"
                          multiple
                          className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <label className="flex items-center space-x-2 mt-2">
                          <input
                            type="checkbox"
                            className="w-5 h-5 text-blue-500 bg-gray-700 border-gray-600 rounded"
                            defaultChecked={item.alerts || false}
                          />
                          <span className="text-white">Enable Alerts / Notifications</span>
                        </label>
                      </div>
                      {/* Section 7: My Consultant */}
                      <div className="bg-gray-800 p-3 rounded-md space-y-4">
                        <h4 className="text-blue-400 font-semibold text-sm sm:text-base">
                          My Consultant
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="Consultant Name"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.name || ""}
                          />
                          <input
                            type="text"
                            placeholder="Contact Person"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.contactPerson || ""}
                          />
                          <input
                            type="text"
                            placeholder="City"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.city || ""}
                          />
                          <input
                            type="text"
                            placeholder="State"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.state || ""}
                          />
                          <input
                            type="text"
                            placeholder="PIN"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.pin || ""}
                          />
                          <input
                            type="text"
                            placeholder="Address"
                            className="w-full sm:col-span-2 p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.address || ""}
                          />
                          <input
                            type="email"
                            placeholder="Email(s)"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.emails || ""}
                          />
                          <input
                            type="text"
                            placeholder="Mobile(s)"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.mobiles || ""}
                          />
                          <input
                            type="text"
                            placeholder="WhatsApp"
                            className="w-full p-2 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            defaultValue={item.consultant?.whatsapp || ""}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                ))}
          </>
        )}        
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}